import { useState, useEffect } from 'react';
import { Copy, Check, Mail, Share, Loader2 } from 'lucide-react';
import { sendVideoEmail } from '@/services/emailService';
import { useToast } from './ToastProvider';
import './VideoPreviewModal.css';

interface VideoPreviewModalProps {
    blob: Blob;
    videoUrl: string;
    productName: string;
    onClose: () => void;
}

export default function VideoPreviewModal({ blob, videoUrl, productName, onClose }: VideoPreviewModalProps) {
    const [localVideoUrl, setLocalVideoUrl] = useState<string | null>(null);
    const [isEmailing, setIsEmailing] = useState(false);
    const [copied, setCopied] = useState(false);
    const { showToast } = useToast();

    // Create a local object URL for the blob to play it immediately without downloading
    useEffect(() => {
        const url = URL.createObjectURL(blob);
        setLocalVideoUrl(url);

        return () => {
            URL.revokeObjectURL(url);
        };
    }, [blob]);

    // Construct the viewer URL for sharing/emailing (same logic as emailService)
    const viewerUrl = `${window.location.origin}/viewer?url=${encodeURIComponent(videoUrl)}${productName ? `&product=${encodeURIComponent(productName)}` : ''}`;

    const handleCopyLink = () => {
        navigator.clipboard.writeText(viewerUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        showToast('Link copied to clipboard!', 'success');
    };

    const handleShare = async () => {
        const shareData = {
            title: `360° Product Video${productName ? ` - ${productName}` : ''}`,
            text: 'Check out my 360° product video!',
            url: viewerUrl,
        };

        if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
            try {
                await navigator.share(shareData);
            } catch (err: any) {
                if (err.name !== 'AbortError') {
                    console.error('Error sharing:', err);
                    handleCopyLink();
                }
            }
        } else {
            handleCopyLink();
        }
    };

    const handleEmail = async () => {
        setIsEmailing(true);
        try {
            await sendVideoEmail(videoUrl, undefined, productName);
            showToast('Video link has been emailed successfully!', 'success');
        } catch (error: any) {
            console.error('Failed to email video:', error);
            showToast(`Failed to email video: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            setIsEmailing(false);
        }
    };

    return (
        <>
            <div className="video-modal-backdrop" onClick={onClose} />
            <div className="video-modal-popup">
                <div className="video-modal-header">
                    <span>360° Video Ready</span>
                    <div className="video-header-actions">
                        <button 
                            className="video-header-btn" 
                            onClick={handleEmail} 
                            disabled={isEmailing}
                            title="Email Link"
                        >
                            {isEmailing ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                        </button>
                        <button 
                            className="video-header-btn" 
                            onClick={handleShare}
                            title="Share"
                        >
                            <Share size={18} />
                        </button>
                        <button 
                            className="video-header-btn" 
                            onClick={handleCopyLink}
                            title={copied ? 'Copied!' : 'Copy Link'}
                        >
                            {copied ? <Check size={18} /> : <Copy size={18} />}
                        </button>
                        <button className="video-header-btn close-btn" onClick={onClose} aria-label="Close">×</button>
                    </div>
                </div>
                
                <div className="video-modal-content">
                    <div className="video-player-container">
                        {localVideoUrl ? (
                            <video 
                                src={localVideoUrl} 
                                controls 
                                autoPlay 
                                loop 
                                playsInline 
                            />
                        ) : (
                            <div style={{ padding: '40px', color: '#fff' }}>Loading video...</div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
